import React from "react";
import { BadgeCheck } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { resolveImage } from "@/lib/media";
import { Badge } from "@/components/ui/badge";
import { OfferBadge } from "@/components/OfferBadge";
import { isWhiteLabelBusiness } from "@/lib/whiteLabel";
import { ratioToPercent } from "@/lib/format";
import { businessKindSlug } from "@/storefront/registry";

/** "Restaurant in Biswan" — a REAL default eyebrow for a free-tier business built from data
 * already on the payload (`business_type` + the tenant/city name), never the generic
 * "BUSINESS MINI-SITE" string. `profile.eyebrow` (owner-entered, ≤80 chars) takes priority when
 * set. White-label businesses keep using `hero_tagline` instead (unchanged). Storefront S1, B2. */
function humaniseBusinessType(businessType) {
  return String(businessType || "")
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function eyebrowFor(business, tenant, t, lang) {
  const profile = business.storefront_config?.profile;
  // R4-6: the owner's Hindi hero line (`eyebrow_hi`) wins on the Hindi site, falling back to English.
  const profileEyebrow = profile ? localized(lang, profile, "eyebrow") : "";
  if (profileEyebrow) return profileEyebrow;
  // R3-9: the kind and the city go through i18n / `name_hi` — "Isp" and "BISWAN में ISP" were English.
  const kind = businessKindSlug(business);
  const typeKey = `storefront.businessTypes.${kind}`;
  const translated = kind ? t(typeKey) : typeKey;
  const category =
    String(localized(lang, business, "display_category") || "").trim() ||
    (typeof translated === "string" && translated !== typeKey
      ? translated
      : humaniseBusinessType(kind));
  const city = localized(lang, tenant, "name");
  if (!category) return t("business.miniSiteBadge");
  return t("storefront.eyebrowDefault", { category, city });
}

/** Cover image, eyebrow, H1 name, trust chips. Storefront S5: the handoff's hero chip row is
 * exactly Verified, a single "★ rating · N reviews" chip, up to 3 owner-entered `profile.chips`
 * and the first live offer — no separate multi-star rating widget and no Favorite/Share buttons
 * in this row (`design_handoff_business_websites/*.dc.html` HERO section, every vertical).
 * Favourite/Share moved to `ContactRail` (S5 gap review item 3) since the handoff has no other
 * spot for them and dropping the feature entirely would be a regression, not a redesign. */
export function StoreHero({ business, offers }) {
  const { t, lang, tenant } = useApp();
  const whiteLabel = isWhiteLabelBusiness(business);
  const chips = business.storefront_config?.profile?.chips || [];
  // RF-20: owner-entered chips carry an optional Hindi sibling (`chips_hi`, same positions).
  const chipsHi = business.storefront_config?.profile?.chips_hi || [];

  return (
    <section
      className={`relative h-[46vh] min-h-[300px] w-full overflow-hidden ${
        // W-20: with no cover image, the section had no background of its own — just a dark
        // gradient over whatever page background showed through, reading as an empty grey box
        // behind the white hero text. A tenant-coloured fallback keeps the hero looking intentional.
        business.cover_image_url ? "" : "bg-[hsl(var(--primary))]"
      }`}
    >
      {business.cover_image_url && (
        <img
          src={resolveImage(business.cover_image_url)}
          alt={business.name}
          className="h-full w-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-5 pb-8 sm:px-8">
        <span className="text-xs font-semibold uppercase tracking-widest text-white/70">
          {whiteLabel ? localized(lang, business, "hero_tagline") : eyebrowFor(business, tenant, t, lang)}
        </span>
        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">
          {localized(lang, business, "name")}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {business.is_verified && (
            <Badge className="gap-1 bg-white/15 text-white backdrop-blur">
              <BadgeCheck size={13} /> {t("common.verified")}
            </Badge>
          )}
          {business.rating_count > 0 && (
            <span
              className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur"
              data-testid="hero-rating-chip"
            >
              {t("storefront.reviewsSummary", {
                rating: Number(business.rating_avg).toFixed(1),
                count: business.rating_count,
              })}
            </span>
          )}
          {chips.map((chip, i) => (
            <span
              key={i}
              data-testid={`hero-profile-chip-${i}`}
              className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur"
            >
              {(lang === "hi" && chipsHi[i]) || chip}
            </span>
          ))}
          {business.completion_rate !== undefined && (
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
              {t("trust.completionRate")}: {ratioToPercent(business.completion_rate)}%
            </span>
          )}
          {business.repeat_customers !== undefined && (
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
              {t("trust.repeatCustomers")}: {business.repeat_customers}
            </span>
          )}
          {offers[0] && <OfferBadge offer={offers[0]} />}
        </div>
      </div>
    </section>
  );
}
